// Token storage keys
const ACCESS_TOKEN_KEY = 'token'; // Using 'token' to match what our WebSocket code expects
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user_data';

// Set tokens in local storage
export const setTokens = (accessToken, refreshToken) => {
  if (!accessToken) {
    console.error('No access token provided to setTokens');
    return;
  }
  
  // Log the token format being used
  const isUsingStandardFormat = accessToken !== refreshToken;
  console.log(`Setting tokens: ${isUsingStandardFormat ? 'Standard format (access + refresh)' : 'Single token format'}`);
  
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  
  // If no refresh token is provided, use access token as both
  if (!refreshToken) {
    console.log('No refresh token provided, using access token for both');
    localStorage.setItem(REFRESH_TOKEN_KEY, accessToken);
  } else {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
};

// Get access token from local storage
export const getAccessToken = () => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

// Get refresh token from local storage
export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

// Set user data in local storage
export const setUserData = (userData) => {
  localStorage.setItem(USER_KEY, JSON.stringify(userData));
};

// Get user data from local storage
export const getUserData = () => {
  const userData = localStorage.getItem(USER_KEY);
  return userData ? JSON.parse(userData) : null;
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!getAccessToken();
};

// More thorough check for a valid token
export const hasValidToken = () => {
  const token = getAccessToken();
  if (!token) return false;
  
  try {
    // Check if token is a valid JWT format (header.payload.signature)
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('Token does not appear to be a valid JWT format');
      return false;
    }
    
    // Try to decode the payload
    const payload = JSON.parse(atob(parts[1]));
    
    // Check if token is expired
    const expiryTime = payload.exp * 1000; // Convert to milliseconds
    if (expiryTime < Date.now()) {
      console.warn('Token appears to be expired');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error validating token format:', error);
    return false;
  }
};

// Clear tokens from storage
export const clearTokens = () => {
  console.log('Clearing all authentication tokens and user data');
  
  // Remove all auth-related items
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  
  // Double-check that tokens are actually cleared
  const accessTokenRemoved = !localStorage.getItem(ACCESS_TOKEN_KEY);
  const refreshTokenRemoved = !localStorage.getItem(REFRESH_TOKEN_KEY);
  const userDataRemoved = !localStorage.getItem(USER_KEY);
  
  console.log('Token removal status:', {
    accessTokenRemoved,
    refreshTokenRemoved,
    userDataRemoved
  });
  
  // If any token wasn't removed, try again with a different approach
  if (!accessTokenRemoved || !refreshTokenRemoved || !userDataRemoved) {
    console.warn('Some tokens were not cleared on first attempt, trying alternate method');
    
    // Try direct assignment to null/empty first (helps with some browser quirks)
    if (!accessTokenRemoved) localStorage.setItem(ACCESS_TOKEN_KEY, '');
    if (!refreshTokenRemoved) localStorage.setItem(REFRESH_TOKEN_KEY, '');
    if (!userDataRemoved) localStorage.setItem(USER_KEY, '');
    
    // Then try remove again
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }
  
  // Dispatch storage event to notify all tabs/components
  window.dispatchEvent(new Event('storage'));
  
  // Return true if all tokens were cleared
  return !localStorage.getItem(ACCESS_TOKEN_KEY) && 
         !localStorage.getItem(REFRESH_TOKEN_KEY) &&
         !localStorage.getItem(USER_KEY);
}; 